import clsx from 'clsx'

type HeadingProps = { level?: 1 | 2 | 3 | 4 | 5 | 6 } & React.ComponentPropsWithoutRef<
  'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
>

/**
 * A page title.
 *
 * `display-3` rather than a raw size: the display scale existed but was used
 * only on the public marketing pages, so every dashboard page title was
 * `text-2xl font-semibold` on the interface font while the same heading on a
 * lodge's public profile got Archivo. One app, two typographic voices.
 *
 * It also used to be `text-2xl/8 sm:text-xl/8`, which is 24px on a phone and
 * 20px on a 1440px monitor - the scale ran backwards, so titles shrank as the
 * screen grew. `display-3` clamps upward instead, 20px to 26px.
 *
 * display-3 and not display-2: display-2 reaches 40px at 1440px, which is the
 * size a marketing page uses for a section title. These pages are rosters and
 * voucher tables, and a 40px "Members" above a dense table reads as a landing
 * page that lost its way.
 */
export function Heading({ className, level = 1, ...props }: HeadingProps) {
  let Element: `h${typeof level}` = `h${level}`

  return (
    <Element {...props} className={clsx(className, 'display-3 text-[color:var(--theme-fg)]')} />
  )
}

/**
 * A card or panel title.
 *
 * Fixed at 16px on the interface font, and deliberately *not* on the display
 * scale: display-3 would make it the same size and face as the page title
 * above it, which is what this shipped as. It was also `text-base/7
 * sm:text-sm/6` before, shrinking from 16px to 14px as the screen grew, the
 * same inversion Heading had.
 */
export function Subheading({ className, level = 2, ...props }: HeadingProps) {
  let Element: `h${typeof level}` = `h${level}`

  return (
    <Element
      {...props}
      className={clsx(className, 'text-base font-semibold text-[color:var(--theme-fg)]')}
    />
  )
}
